import React, { useState } from "react";
import { useCombinedCartAndMenuData } from "./combinedCartAndMenuData";
import { CartItemEditForm } from "./CartItemEditForm";
import { OrderConfirm } from "./OrderConfirm";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Step,
  StepButton,
  Stepper,
  Typography,
} from "@mui/material";
import { Cancel } from "@mui/icons-material";
import { CartItem } from "./cart-provider/CartProvider";
import { OrderType } from "../types/OrderType";

const CART_TAB_VALUE = 0;
const CONFIRM_TAB_VALUE = 1;

export const Cart = ({
  onOrderPlaced,
  onClose,
}: {
  onOrderPlaced: (order: OrderType) => void;
  onClose: () => void;
}) => {
  const [activeStep, setActiveStep] = useState(CART_TAB_VALUE);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        padding: 2,
        boxSizing: "border-box",
      }}
    >
      <Box display="flex" alignItems="center" mb={1}>
        <Stepper activeStep={activeStep} nonLinear sx={{ flex: 1 }}>
          <Step key={"Cart"}>
            <StepButton
              color="inherit"
              onClick={() => setActiveStep(CART_TAB_VALUE)}
              data-testid="test-id-cart-title"
            >
              Cart
            </StepButton>
          </Step>

          <Step key={"Confirm"}>
            <StepButton
              color="inherit"
              onClick={() => setActiveStep(CONFIRM_TAB_VALUE)}
            >
              Confirm
            </StepButton>
          </Step>
        </Stepper>

        <IconButton
          onClick={onClose}
          data-testid="cart-pop-up-close-button"
          size="small"
        >
          <Cancel fontSize="large" />
        </IconButton>
      </Box>

      {activeStep == CART_TAB_VALUE && (
        <CartItems onProceed={() => setActiveStep(CONFIRM_TAB_VALUE)} />
      )}
      {activeStep == CONFIRM_TAB_VALUE && (
        <OrderConfirm
          onSuccess={onOrderPlaced}
          onBack={() => setActiveStep(CART_TAB_VALUE)}
        />
      )}
    </Box>
  );
};

const OrderSummary = ({
  currency,
  taxPercentage,
  cartItemList,
  isItemWiseTaxEnabled,
}: {
  currency: string;
  taxPercentage: number;
  cartItemList: CartItem[];
  isItemWiseTaxEnabled: boolean;
}) => {
  return (
    <Box
      display="flex"
      flexDirection={"column"}
      marginBottom={2}
      marginTop={2}
      data-testid="test-id-customer-order-summary"
    >
      <SummaryRow
        label="Subtotal"
        value={`${currency}${calculateSubTotal(cartItemList).toFixed(2)}`}
        currency={currency}
      />
      <SummaryRow
        label={isItemWiseTaxEnabled ? "Tax" : `Tax (${taxPercentage}%)`}
        value={`${currency}${calculateTax(
          cartItemList,
          taxPercentage,
          isItemWiseTaxEnabled
        ).toFixed(2)}`}
        currency={currency}
      />
      <SummaryRow
        label="Grand Total"
        value={`${currency}${calculateGrandTotal(
          cartItemList,
          taxPercentage,
          isItemWiseTaxEnabled
        ).toFixed(2)}`}
        currency={currency}
        bold
      />
    </Box>
  );
};

const SummaryRow = ({
  label,
  value,
  bold,
}: {
  label: string;
  value: string;
  currency: string;
  bold?: boolean;
}) => (
  <Box display="flex" justifyContent="space-between" px={1}>
    <Typography fontWeight={bold ? 600 : 400}>{label}:</Typography>
    <Typography fontWeight={bold ? 600 : 400}>{value}</Typography>
  </Box>
);

const CartItems = ({ onProceed }: { onProceed: () => void }) => {
  const {
    cartItemList,
    increaseItemQuantity,
    removeFromCart,
    updateNoteInCart,
    currency,
    taxPercentage,
    isItemWiseTaxEnabled,
  } = useCombinedCartAndMenuData();

  return (
    <Box
      display="flex"
      flexDirection="column"
      flex={1}
      overflow="hidden"
      mt={1}
    >
      <Box flex={1} sx={{ overflowY: "auto" }}>
        {cartItemList.map((value, index) => {
          return (
            <div key={index} data-testid="cart-item-row">
              <CartItemEditForm
                id={value.item.id}
                itemName={value.item.name}
                note={value.note}
                quantity={value.quantity}
                description={value.item.description}
                price={value.item.price}
                currency={currency}
                onDecreaseItemQuantity={removeFromCart}
                onIncreaseItemQuantity={increaseItemQuantity}
                onUpdateNoteInCart={updateNoteInCart}
              />

              <Divider />
            </div>
          );
        })}
        <OrderSummary
          currency={currency}
          taxPercentage={taxPercentage}
          cartItemList={cartItemList}
          isItemWiseTaxEnabled={isItemWiseTaxEnabled}
        />
      </Box>

      <Box display="flex" justifyContent="center" pt={1.5}>
        <Button
          onClick={onProceed}
          variant="contained"
          size="medium"
          sx={{ width: "140px" }}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
};

const calculateSubTotal = (cartItemList: CartItem[]) => {
  let subTotal = 0.0;
  for (let i = 0; i < cartItemList.length; i++) {
    subTotal += cartItemList[i].item.price * cartItemList[i].quantity;
  }
  return subTotal;
};

const calculateTax = (
  cartItemList: CartItem[],
  taxPercentage: number,
  isItemWiseTaxEnabled: boolean
) => {
  if (!isItemWiseTaxEnabled) {
    const subTotal = calculateSubTotal(cartItemList);
    const tax = (subTotal * taxPercentage) / 100;
    return tax;
  }

  let taxTotal = 0.0;
  for (let i = 0; i < cartItemList.length; i++) {
    const itemTotal = cartItemList[i].item.price * cartItemList[i].quantity;
    const itemTax = cartItemList[i].item.tax;
    if (itemTax !== undefined) taxTotal += (itemTotal * itemTax) / 100;
  }

  return taxTotal;
};

const calculateGrandTotal = (
  cartItemList: CartItem[],
  taxPercentage: number,
  isItemWiseTaxEnabled: boolean
) => {
  const subTotal = calculateSubTotal(cartItemList);
  const tax = calculateTax(cartItemList, taxPercentage, isItemWiseTaxEnabled);
  return subTotal + tax;
};
